<script>
  import { onMount } from "svelte";
  import { auth, initAuth, login } from "./lib/auth/auth.svelte.js";
  import { clients, activeClient, loadClients } from "./lib/clients.svelte.js";
  import { permissions, loadPermissions } from "./lib/permissions.svelte.js";
  import { router } from "@bitcashier/shared/router/router.svelte.js";
  import Router from "./lib/router/Router.svelte";
  import Shell from "./lib/layout/Shell.svelte";
  import Loader from "./lib/ui/Loader.svelte";
  import ViewAsBanner from "./lib/layout/ViewAsBanner.svelte";
  import { ROUTES, PUBLIC_PATHS } from "./routes.js";
  import { t } from "./lib/i18n/i18n.svelte.js";
  import { env } from "./lib/core/env.js";
  import { setActiveSentryUser } from "./lib/observability/sentry.js";
  import { initAnalytics, pagePath, trackPageview } from "./lib/observability/analytics.js";
  import { startViewingAsFromUrl, viewAsEntry, withViewAsRequest } from "./lib/viewAs.svelte.js";

  // Paths that render without the shell / without requiring a session. /health is the static
  // probe endpoint React answered with an empty element.
  const isPublic = $derived(PUBLIC_PATHS.includes(router.path));

  onMount(initAuth);

  // Google Analytics, replacing React's useGaTracker() in layout.jsx: initialised once with the
  // active client as the analytics user id (React read the same `active-client` cookie, which is
  // where clients.activeId starts from), then one pageview per route change. `gaKey` is set for
  // production builds only — everywhere else initAnalytics() injects nothing at all.
  onMount(() => {
    initAnalytics(env.gaKey, clients.activeId);
  });

  $effect(() => {
    // The router owns the path; the query string is not part of its state, so it is read at send
    // time — react-ga was given `pathname + search` and GA4's page_path keeps that shape.
    trackPageview(pagePath(router.path, window.location.search));
  });

  // Tag Sentry events with the client being acted on (id + name), so an error report says whose
  // account it happened on — layout.jsx did this from the same place, as the active client resolved.
  $effect(() => {
    const client = activeClient();
    setActiveSentryUser(client?.id, client?.name);
  });

  // The guard that replaced ProtectedComponent: once auth resolves, an unauthenticated user on a
  // protected path is sent through Auth0 login (returning to where they were). `auth.error` is a
  // sign-in that already failed twice in this tab — sending it back to Auth0 is the loop the
  // second attempt stopped, so that case gets the screen below instead.
  $effect(() => {
    if (!auth.loading && !auth.isAuthenticated && !auth.error && !isPublic) {
      login(withViewAsRequest(router.path));
    }
  });

  // A Super admin arriving from the admin panel's "View dashboard as" (`?viewAs=<userId>`): start
  // the view before anything boots, since starting it reloads the page.
  $effect(() => {
    if (auth.isAuthenticated && !isPublic && viewAsEntry.status === "requested") {
      startViewingAsFromUrl();
    }
  });

  // Boot sequence, mirroring src/user/modules/layout_elements/layout.jsx: the clients list and the
  // permissions load in PARALLEL (permissions are scoped by the active-client cookie, which a
  // returning user already has), and the app renders only once both have resolved and an active
  // client is known.
  $effect(() => {
    if (auth.isAuthenticated && !isPublic && viewAsEntry.status === "done" && !clients.requested) {
      loadClients();
      if (clients.activeId) loadPermissions();
    }
  });

  // First-time users have no cookie, so permissions can only be fetched once the active client is
  // derived from the list. Re-fetching on a client switch is handled by the switcher itself.
  $effect(() => {
    if (auth.isAuthenticated && clients.activeId && !permissions.loaded && clients.loaded) {
      loadPermissions();
    }
  });

  const booted = $derived(clients.loaded && permissions.loaded && Boolean(activeClient()));

  function retryClients() {
    clients.error = null;
    clients.loaded = false;
    loadClients();
  }

  function retrySignIn() {
    auth.error = null;
    login("/");
  }
</script>

{#if router.path === "/health"}
  <!-- Health probe endpoint: render nothing, 200 from the static host. -->
{:else if auth.loading}
  <Loader fullScreen />
{:else if isPublic}
  <Router routes={ROUTES} fallback="/" />
{:else if auth.isAuthenticated}
  {#if booted}
    <Shell>
      <Router routes={ROUTES} fallback="/" />
    </Shell>
  {:else if clients.error}
    <!-- Viewing as someone ends from the banner, so it has to be here too, outside the shell. -->
    <ViewAsBanner />
    <!-- The list could not be fetched: a failure and an empty account used to render the same
         "no clients" text, which sent us hunting for a missing client when the real answer was in
         the response. Keep them apart, and offer the retry the user would otherwise get by
         reloading. -->
    <div class="empty" style="padding-top:80px">
      <div>{t("clients_load_failed")}</div>
      <button class="btn secondary" style="margin-top:12px" onclick={retryClients}>
        {t("try_again")}
      </button>
    </div>
  {:else if clients.loaded && !activeClient()}
    <!-- The account has no client assigned. React spun on a loader here forever; say so instead. -->
    <ViewAsBanner />
    <div class="empty" style="padding-top:80px">{t("no_clients_exists")}</div>
  {:else}
    <Loader fullScreen />
  {/if}
{:else if auth.error}
  <!-- Auth0 sent us back without a session twice in a row (a spent or expired authorization code,
       a refused login, a token request that got no answer). One more automatic attempt has already
       been made; a loader here would spin forever and another redirect would loop, so say it and
       let the user start the sign-in again. -->
  <div class="empty" style="padding-top:80px">
    <div>{t("sign_in_failed")}</div>
    <button class="btn secondary" style="margin-top:12px" onclick={retrySignIn}>
      {t("try_again")}
    </button>
  </div>
{:else}
  <Loader fullScreen />
{/if}
